import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import RatingStars from "@/components/RatingStars";
import { Trash2, MessageSquare, PencilLine } from "lucide-react";

/**
 * Reusable reviews block. Works for profiles (entity="profile") and hotels
 * (entity="hotel"). Endpoint shapes are normalized here.
 */
export default function ReviewsSection({ entity, entityId, ratingAvg = 0, ratingCount = 0, onChange }) {
  const { user } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(false);

  const endpointList = entity === "profile"
    ? `/reviews/profile/${entityId}`
    : `/hotels/${entityId}/reviews`;
  const endpointCreate = entity === "profile"
    ? `/reviews`
    : `/hotels/${entityId}/reviews`;

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(endpointList);
      setReviews(data || []);
      // Prefill form if user already reviewed
      const mine = (data || []).find((r) => r.author_user_id === user?.id);
      if (mine) {
        setRating(mine.rating);
        setComment(mine.comment || "");
        setEditing(true);
      }
    } finally { setLoading(false); }
  };

  useEffect(() => { if (entityId) load(); /* eslint-disable-next-line */ }, [entityId]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (rating < 1) { setError("Elige al menos 1 estrella."); return; }
    try {
      const body = entity === "profile"
        ? { profile_id: entityId, rating, comment }
        : { rating, comment };
      await api.post(endpointCreate, body);
      await load();
      if (onChange) onChange();
    } catch (e) {
      setError(e.response?.data?.detail || "Error al enviar reseña.");
    }
  };

  const remove = async (reviewId) => {
    if (!window.confirm("¿Eliminar esta reseña?")) return;
    const url = entity === "profile"
      ? `/reviews/${reviewId}`
      : `/hotels/${entityId}/reviews/${reviewId}`;
    await api.delete(url);
    await load();
    if (onChange) onChange();
  };

  const isOwner = user && reviews.some((r) => r.author_user_id === user.id);

  return (
    <div
      className="p-6"
      style={{ background: "var(--bg-surface)", border: "1px solid var(--border-accent)" }}
      data-testid={`reviews-${entity}`}
    >
      <div className="flex items-center justify-between mb-5 gap-4 flex-wrap">
        <div>
          <div className="overline" style={{ color: "var(--brand-primary)" }}>Reseñas</div>
          <div className="flex items-baseline gap-3 mt-1">
            <span className="font-serif text-3xl" style={{ color: "var(--text-primary)" }}>
              {ratingAvg > 0 ? ratingAvg.toFixed(1) : "—"}
            </span>
            <RatingStars value={ratingAvg} size={16} testid="reviews-avg" />
            <span className="text-xs" style={{ color: "var(--text-muted)" }}>
              {ratingCount} reseña{ratingCount === 1 ? "" : "s"}
            </span>
          </div>
        </div>
      </div>

      {user && user.id !== null ? (
        <form onSubmit={submit} className="flex flex-col gap-3 mb-6 pb-6" style={{ borderBottom: "1px solid var(--border-subtle)" }} data-testid="review-form">
          <div className="flex items-center justify-between">
            <span className="text-xs uppercase tracking-widest" style={{ color: "var(--text-secondary)" }}>
              {editing ? "Editar tu reseña" : "Deja tu reseña"}
            </span>
            <RatingStars value={rating} onChange={setRating} size={22} testid="review-input" />
          </div>
          <textarea
            className="input-field"
            rows={3}
            placeholder="Escribe tu opinión (opcional)…"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            maxLength={entity === "hotel" ? 800 : 1000}
            data-testid="review-comment"
          />
          {error && <p className="text-xs" style={{ color: "#ef6d6d" }} data-testid="review-error">{error}</p>}
          <div className="flex items-center gap-3">
            <button type="submit" className="btn-primary text-xs inline-flex items-center gap-2" data-testid="review-submit">
              {editing ? <><PencilLine size={12} /> Actualizar</> : <><MessageSquare size={12} /> Publicar reseña</>}
            </button>
            <span className="text-xs" style={{ color: "var(--text-muted)" }}>
              {comment.length}/{entity === "hotel" ? 800 : 1000}
            </span>
          </div>
        </form>
      ) : (
        <div
          className="p-4 mb-5 flex items-center justify-between gap-3 flex-wrap"
          style={{
            background: "var(--bg-elevated)",
            border: "1px solid var(--brand-primary)",
            borderRadius: "2px",
          }}
          data-testid="reviews-login-cta"
        >
          <p className="text-sm" style={{ color: "var(--text-secondary)" }}>
            <MessageSquare size={14} className="inline mr-2" style={{ color: "var(--brand-primary)" }} />
            Inicia sesión para dejar tu reseña.
          </p>
          <a href="/login" className="btn-primary text-xs" data-testid="reviews-login-btn">
            Iniciar sesión
          </a>
        </div>
      )}

      {loading ? (
        <p className="text-sm" style={{ color: "var(--text-muted)" }}>Cargando…</p>
      ) : reviews.length === 0 ? (
        <p className="text-sm" style={{ color: "var(--text-muted)" }} data-testid="reviews-empty">
          Aún no hay reseñas. Sé el primero.
        </p>
      ) : (
        <div className="flex flex-col gap-4" data-testid="reviews-list">
          {reviews.map((r) => {
            const canDelete = user && (r.author_user_id === user.id || user.role === "admin" || user.role === "moderator");
            return (
              <div key={r.id} className="pb-4" style={{ borderBottom: "1px solid var(--border-subtle)" }} data-testid={`review-item-${r.id}`}>
                <div className="flex items-start justify-between gap-3 mb-1">
                  <div>
                    <span className="font-serif text-sm" style={{ color: "var(--text-primary)" }}>
                      {r.author_name}
                    </span>
                    {r.author_role === "admin" && <span className="ml-2 text-[10px] font-bold" style={{ color: "var(--vip-gold)" }}>ADMIN</span>}
                    {r.author_role === "moderator" && <span className="ml-2 text-[10px] font-bold" style={{ color: "var(--brand-primary)" }}>MOD</span>}
                  </div>
                  <div className="flex items-center gap-2">
                    <RatingStars value={r.rating} size={12} testid={`review-rating-${r.id}`} />
                    {canDelete && (
                      <button
                        onClick={() => remove(r.id)}
                        className="p-1"
                        style={{ color: "var(--text-muted)" }}
                        aria-label="Eliminar"
                        data-testid={`review-delete-${r.id}`}
                      >
                        <Trash2 size={12} />
                      </button>
                    )}
                  </div>
                </div>
                {r.comment && (
                  <p className="text-sm mt-1" style={{ color: "var(--text-secondary)" }}>
                    {r.comment}
                  </p>
                )}
                <p className="text-[10px] mt-2" style={{ color: "var(--text-muted)" }}>
                  {new Date(r.created_at).toLocaleDateString("es-MX", { day: "numeric", month: "short", year: "numeric" })}
                  {r.updated_at && r.updated_at !== r.created_at && " (editada)"}
                </p>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
